// pages/UserAdmin/crm/ContatoExisteDialog.js
//
// O que aparece quando alguém tenta cadastrar um número que a empresa já
// tem.
//
// ─── Por que um modal e não só um erro ─────────────────────────────────
// Out/26. Antes o backend recusava com "Já existe contato com esse número"
// e pronto. A pessoa ficava sem saber QUEM é o contato existente, e a
// saída natural era digitar o número com outra formatação até passar — o
// que criava exatamente a duplicata que a mensagem tentava impedir.
//
// Agora o 409 carrega o contato, e a tela oferece as duas coisas que
// alguém de fato quer nesse momento: abrir o que já existe, ou corrigir o
// nome dele. O que NÃO se pode fazer é criar um segundo registro com nome
// diferente pro mesmo número — é isso que deixava a base com "Marta" e
// "Marta Peixaria" sendo a mesma pessoa.
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material';
import { Icon } from './icons';

const SYNE = "'Syne', system-ui, sans-serif";

/** '5521964338953' → '+55 21 96433-8953'. Só pra leitura. */
function formatar(tel) {
  const d = String(tel || '').replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  return tel || '';
}

export default function ContatoExisteDialog({
  conflito, onClose, onAbrir, onRenomear, salvando,
}) {
  if (!conflito) return null;

  const existente = conflito.contato || {};
  const nomeTentado = (conflito.nome_tentado || '').trim();
  // Só oferece renomear quando o nome digitado é de fato diferente —
  // senão o botão prometeria uma mudança que não acontece.
  const podeRenomear = nomeTentado && nomeTentado !== (existente.name || '');

  return (
    <Dialog open onClose={salvando ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--ch-text)', pb: 1 }}>
        Este número já está cadastrado
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Box sx={{
            border: '1px solid var(--ch-bdr)', borderRadius: 2, p: 2, bgcolor: 'var(--ch-surf)',
          }}>
            <Typography sx={{
              fontFamily: SYNE, fontWeight: 800, fontSize: '1rem', color: 'var(--ch-text)',
            }}>
              {existente.name || '(sem nome)'}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'var(--cx-cap)', mt: 0.3 }}>
              {formatar(existente.phone_number)}
            </Typography>
          </Box>

          {conflito.erro && (
            <Alert severity="error" sx={{ fontSize: '0.82rem' }}>{conflito.erro}</Alert>
          )}

          {podeRenomear ? (
            <Alert severity="info" icon={false} sx={{ fontSize: '0.82rem' }}>
              Você digitou <strong>{nomeTentado}</strong>. Um número só pode ter
              um contato — dá pra corrigir o nome do que já existe, mas não
              criar outro.
            </Alert>
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: 'var(--cx-cap)' }}>
              Abra o contato existente para ver o histórico e editar os dados.
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={onClose}
          disabled={salvando}
          sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: 'var(--cx-cap)' }}
        >
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => onAbrir(existente.id)}
          disabled={salvando}
          variant="outlined"
          sx={{
            textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
            borderColor: 'var(--ch-bdr2)', color: 'var(--ch-text)', borderRadius: 2,
          }}
        >
          Abrir contato
        </Button>
        {podeRenomear && (
          <Button
            onClick={() => onRenomear(existente.id, nomeTentado)}
            disabled={salvando}
            variant="contained"
            disableElevation
            startIcon={<Icon.Check size={14} />}
            sx={{
              textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
            }}
          >
            {salvando ? 'Salvando…' : `Renomear para "${nomeTentado}"`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
